/**
 * Managed agents — user-defined agents configured in Settings.
 *
 * A managed agent is a saved bundle of "how to run a turn": which provider and
 * model to talk to, what standing instructions to prepend, which tools the
 * session may touch, who is allowed to see it (teams), and which other managed
 * agents it may delegate work to (subagents). Selecting one in a thread's model
 * picker is what applies the bundle; nothing here runs on its own.
 *
 * Named `ManagedAgent*` rather than `Agent*` on purpose. Two unrelated "agent"
 * concepts already exist in this repo and both are reachable from the same
 * files: `@taskin-code/shared/agents` (the CLI launcher catalog — `claude`,
 * `codex` binaries spawned into a terminal) and the provider-side subagent
 * hints in `orchestration.ts` (`subagentAgentId`, the ids Claude assigns to its
 * own in-session sidechains). Reusing the bare name would make imports
 * ambiguous exactly where the distinction matters most. The admin request and
 * response envelopes keep the shorter `AdminAgent*` names because they only
 * ever appear next to their own route handlers, where there is nothing to
 * confuse them with.
 *
 * Not event-sourced, following `Team` in `auth.ts`: these are administrative
 * definitions whose SQL rows are authoritative. The orchestration event log
 * stays reserved for thread work.
 *
 * Membership-shaped fields (`teamIds`, `subagentIds`) travel as flat id lists
 * rather than embedded objects so the same payload can serve an admin editing
 * the agent and a picker that only needs to know what it may select. Order of
 * `subagentIds` is meaningful — it is the order the delegation tools present
 * them in — so it is preserved end to end.
 */
import { Schema } from "effect";

import { IsoDateTime, ManagedAgentId, TagId, TeamId, TrimmedNonEmptyString } from "./baseSchemas";
import { ProviderModelOptions } from "./model";
import { ProviderKind } from "./orchestration";

export const AGENT_NAME_MAX_LENGTH = 80;

/**
 * Names are trimmed, non-empty and bounded so the unique index on live rows
 * (`idx_agents_name_active`) matches what the UI can actually produce.
 */
export const ManagedAgentName = TrimmedNonEmptyString.check(
  Schema.isMaxLength(AGENT_NAME_MAX_LENGTH),
);

/**
 * Ids of tools the agent may use, from the selectable catalog. Kept as opaque
 * strings rather than a literal union: the catalog is provider-dependent and
 * grows with every SDK release, and a stored definition must survive a tool
 * disappearing from the catalog rather than failing to decode. Enforcement of
 * "is this a known tool?" belongs to the write path, not to the row schema.
 */
export const ManagedAgentToolId = TrimmedNonEmptyString;

/**
 * Instructions are unbounded on purpose — this is the agent's system prompt
 * append, and users paste whole playbooks into it.
 */
export const ManagedAgentInstructions = Schema.String;

/**
 * Which ways an agent may be run.
 *
 * - `primary`: a person picks it — a new thread, the chat's model picker, a fork
 *   or a handoff. It may *not* be another agent's subagent.
 * - `subagent`: only reachable through delegation. Never offered in a picker,
 *   and never in a side chat either: a side chat is a person choosing from a
 *   menu, which is precisely what this mode says never happens.
 * - `general`: both.
 * - `coordinator`: a user picks it only to open initiatives in a project; it
 *   delegates to primary agents, but is never a subagent or side chat.
 *
 * One enum rather than a set of per-surface booleans because the answer is a
 * single question with four legible answers, and a boolean set admits the
 * illegal "none of them" state. `sideChatEnabled` stays a separate flag because
 * it is an opt-in *within* the surfaces this enum already allows, not a fourth
 * mode — an agent can be perfectly runnable and still be a terrible side chat.
 *
 * The default is `general` everywhere, including the migration backfill: every
 * agent that predates this field was usable in every way, and narrowing it
 * retroactively would empty pickers and break live delegations on deploy.
 *
 * Surface-by-mode decisions live in `@taskin-code/shared/managedAgentMode`, not
 * here — this package stays schema-only.
 */
export const ManagedAgentMode = Schema.Literals(["primary", "subagent", "general", "coordinator"]);
export type ManagedAgentMode = typeof ManagedAgentMode.Type;

export const ManagedAgent = Schema.Struct({
  id: ManagedAgentId,
  name: ManagedAgentName,
  // Null rather than absent: this mirrors `roleLabel` on `AuthenticatedUser`
  // and maps one-to-one onto the nullable SQL column, so a response never has
  // to distinguish "unset" from "not sent".
  description: Schema.NullOr(Schema.String),
  provider: ProviderKind,
  model: TrimmedNonEmptyString,
  instructions: ManagedAgentInstructions,
  tools: Schema.Array(ManagedAgentToolId),
  // Empty means "visible to everyone" — a house agent. See the visibility rule
  // in the server's ManagedAgentService.
  teamIds: Schema.Array(TeamId),
  subagentIds: Schema.Array(ManagedAgentId),
  // How this agent may be run. See `ManagedAgentMode`.
  mode: ManagedAgentMode,
  // Whether this agent may be picked for a side chat over a transcript
  // selection. Separate from `subagentIds`: being a good subagent and being a
  // good side chat are different jobs, and the side chat is chosen by a user
  // from a menu rather than by a parent agent following its graph.
  sideChatEnabled: Schema.Boolean,
  // Whether this agent refuses memories and local commands that carry no tags.
  // Tags are default-allow so that an existing catalogue keeps working the day
  // they ship; this is how the agents that need protecting get it without
  // anyone relabelling everything first. See migration 047.
  memoryStrict: Schema.Boolean,
  // Null rather than absent: the dedicated avatar endpoints own this nullable
  // SQL field, so every agent response has one stable shape.
  avatarFileName: Schema.NullOr(Schema.String),
  // Tags this agent carries. They answer "who is working" in the matching rule;
  // the project answers "on what". Empty means the agent imposes no audience of
  // its own and loads whatever its project and `memoryStrict` allow.
  tagIds: Schema.Array(TagId),
  /**
   * How hard the agent thinks: the same `{ codex?, claudeAgent? }` block that
   * travels on `ModelSelection.options`, frozen onto the agent definition.
   *
   * Selecting a managed agent *imposes* this block — the person running the turn
   * stops choosing effort, `thinking` and `fastMode` for it. An agent is a unit of
   * behaviour an admin approves, and how deeply (and expensively) it reasons is
   * part of that behaviour rather than a preference of whoever invokes it. `null`,
   * or a provider key the agent does not use, means "the default of the agent's
   * model" — which is still imposed, not left free.
   *
   * Keyed by provider rather than a bare `CodexModelOptions | ClaudeModelOptions`
   * union: every field in both structs is optional and decoding ignores excess
   * properties, so `{ effort: "max" }` would validate as an empty
   * `CodexModelOptions` and lose the setting without an error. See `model.ts`.
   *
   * Whether a stored effort is one the model still offers is not checked here.
   * The ladders move with each provider release and the model may be a slug typed
   * by hand, so validation belongs to the write path and the adapters degrade to
   * the model default for anything unrecognised.
   */
  modelOptions: Schema.NullOr(ProviderModelOptions),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type ManagedAgent = typeof ManagedAgent.Type;

export const AdminAgentsResponse = Schema.Struct({
  agents: Schema.Array(ManagedAgent),
});
export type AdminAgentsResponse = typeof AdminAgentsResponse.Type;

/** Single-agent envelope returned by create and update. */
export const AdminAgentResponse = Schema.Struct({
  agent: ManagedAgent,
});
export type AdminAgentResponse = typeof AdminAgentResponse.Type;

/**
 * Create takes every field that defines behaviour; the id and timestamps are
 * the server's to mint. The list fields are optional so a caller can create a
 * bare agent and wire teams or subagents later.
 */
export const AdminAgentCreateRequest = Schema.Struct({
  name: ManagedAgentName,
  description: Schema.optional(Schema.NullOr(Schema.String)),
  provider: ProviderKind,
  model: TrimmedNonEmptyString,
  instructions: Schema.optional(ManagedAgentInstructions),
  tools: Schema.optional(Schema.Array(ManagedAgentToolId)),
  teamIds: Schema.optional(Schema.Array(TeamId)),
  subagentIds: Schema.optional(Schema.Array(ManagedAgentId)),
  // Absent means `general`, matching the column default: an agent created by a
  // client that predates this field keeps behaving as every agent did before it.
  mode: Schema.optional(ManagedAgentMode),
  // Absent means off. No agent is offered for a side chat until an admin says
  // so, so the safe default is also the quiet one.
  sideChatEnabled: Schema.optional(Schema.Boolean),
  memoryStrict: Schema.optional(Schema.Boolean),
  // A present list is a full replacement, like `teamIds`.
  tagIds: Schema.optional(Schema.Array(TagId)),
  // Absent or null means "the default of whatever model this agent runs".
  modelOptions: Schema.optional(Schema.NullOr(ProviderModelOptions)),
});
export type AdminAgentCreateRequest = typeof AdminAgentCreateRequest.Type;

/**
 * Update is a patch: every field is optional and an absent field means "leave
 * it alone". A *present* list field is a full replacement, not a merge — the
 * editor always sends the complete roster it is showing, and a merge would make
 * removing the last team or subagent impossible to express.
 */
export const AdminAgentUpdateRequest = Schema.Struct({
  name: Schema.optional(ManagedAgentName),
  description: Schema.optional(Schema.NullOr(Schema.String)),
  provider: Schema.optional(ProviderKind),
  model: Schema.optional(TrimmedNonEmptyString),
  instructions: Schema.optional(ManagedAgentInstructions),
  tools: Schema.optional(Schema.Array(ManagedAgentToolId)),
  teamIds: Schema.optional(Schema.Array(TeamId)),
  subagentIds: Schema.optional(Schema.Array(ManagedAgentId)),
  mode: Schema.optional(ManagedAgentMode),
  sideChatEnabled: Schema.optional(Schema.Boolean),
  memoryStrict: Schema.optional(Schema.Boolean),
  // A present list is a full replacement, like `teamIds`.
  tagIds: Schema.optional(Schema.Array(TagId)),
  // Present-and-null clears the pin back to the model default; absent leaves it
  // alone. A present block is a full replacement, like the list fields.
  modelOptions: Schema.optional(Schema.NullOr(ProviderModelOptions)),
});
export type AdminAgentUpdateRequest = typeof AdminAgentUpdateRequest.Type;
